<script setup>
import {gameScoreText, scorePoints} from '~/utils/gameScore';
import {canDeleteMeetMatch, canDeleteUnplayedMatch, isGeneratedMatch} from '~/utils/matchActions';
import {ladderFixtureSchedule, ladderFixtureViewer} from '~/utils/challengeSchedule';
import {ref, onMounted, onBeforeUnmount, computed} from 'vue';
import {useAuthService} from "@/composables/useAuthService";
import TeamCard from "@/components/teams/TeamCard.vue";
import {useValidation} from "@/composables/useValidation";
import FixtureLadderPositionChange from "~/components/fixtures/FixtureLadderPositionChange.vue";
import {useLocalisedDates} from "~/composables/useLocalisedDates";
const {shortDate} = useLocalisedDates();

const router = useRouter();
const route = useRoute();

const {addListener, removeLocalListenersWithEvent, notify} = useListenerService();
const props = defineProps({
  fixture: {
    type: Object,
    default: () => null,
  },
  allowEditing: {
    type: Boolean,
    default: true,
  },
  simple: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'small',
    validator: (value) => ['small', 'large'].includes(value),
  },
  navigable: {
    type: Boolean,
    default: true,
  },
  // Hairline card border. Off by default — many placements sit inside an
  // already-bordered container, where a second outline reads as a double
  // border. Opt in for standalone usage (e.g. a lone fixture on a page).
  bordered: {
    type: Boolean,
    default: false,
  },
});

const navigateToFixture = () => {
  router.push(`/fixtures/${props.fixture.id}`);
};

const {user, isSuperUser, isAuthenticated, isAdminOfClub} = useAuthService();

const editing = ref(false);
const deleting = ref(false);
const breakdown = ref(false);

const {isPlayerInFixture, fixtureNeedsApprovalFromPlayerId, isFixtureFullyApproved, fixtureHasResult, fixtureResultAction, fixtureResultActionLabel, fixtureResultActionIcon, myTeamFromListOfTeams, fixtureScheduleState, fixtureScheduleActionLabel} = useValidation();
const isFixtureAdmin = computed(() => {
  return isAuthenticated.value && (isSuperUser.value || isAdminOfClub(props.fixture.clubId));
});

// Record result / Approve result / Edit result — approval-aware for the current user.
const resultAction = computed(() =>
  fixtureResultAction(props.fixture, {playerId: user.value?.playerId, isAdmin: isFixtureAdmin.value}));

const showEdit = computed(() => {
  if(props.allowEditing === false) {
    return false;
  }
  return isFixtureAdmin.value || isPlayerInFixture(user.value.playerId, props.fixture);
});
const canEdit = computed(() => {
  if(props.allowEditing === false) {
    return false;
  }
  if (props.fixture != null) {
    if (isFixtureAdmin.value) {
      return props.fixture.adminEditable;
    } else if (isPlayerInFixture(user.value.playerId, props.fixture)) {
      return props.fixture.playerEditable;
    }
  }
  return false;
});

const homeLadderPosition = computed(() => {
  if (props.fixture && props.fixture.ladderPositions) {
    return props.fixture.ladderPositions.find((lp) => lp.teamId === props.fixture.homeTeamId);
  }
  return null;
});

const awayLadderPosition = computed(() => {
  if (props.fixture && props.fixture.ladderPositions) {
    return props.fixture.ladderPositions.find((lp) => lp.teamId === props.fixture.awayTeamId);
  }
  return null;
});

const noEditReason = computed(() => {
  if (props.fixture != null) {
    if (isFixtureAdmin.value) {
      return props.fixture.adminNonEditableReason;
    } else if (isPlayerInFixture(user.value.playerId, props.fixture)) {
      return props.fixture.playerNonEditableReason;
    }
  }
  return false;
});

// Schedule state computeds
// "Has a result" is authoritative on the result fields (matches Flutter Fixture.hasResult()
// and the shared helper). Some fixtures carry an approved result with points but a null
// playedAt/score, so relying on playedAt/score alone wrongly renders them as unplayed.
const isPlayed = computed(() => fixtureHasResult(props.fixture));

// Played date can be null even for an approved result (data quirk) — fall back gracefully.
const playedDateLabel = computed(() =>
  props.fixture?.playedAt ? shortDate(props.fixture.playedAt) : 'Result recorded');

const hasConfirmedSchedule = computed(() => {
  return props.fixture?.scheduledAt != null;
});

const hasProposedSchedule = computed(() => {
  return props.fixture?.homeTeamScheduledAt != null ||
         props.fixture?.awayTeamScheduledAt != null;
});

const hasSchedule = computed(() => {
  return hasConfirmedSchedule.value || hasProposedSchedule.value;
});

const scheduleDateToShow = computed(() => {
  if (hasConfirmedSchedule.value) return props.fixture.scheduledAt;
  if (hasProposedSchedule.value) {
    return props.fixture.homeTeamScheduledAt || props.fixture.awayTeamScheduledAt;
  }
  return null;
});

// Schedule state + wording come from the shared state machine in useValidation
// (mirrored by the mobile app) — do not hand-roll schedule labels here.
const myTeam = computed(() =>
    isAuthenticated.value && user.value
        ? myTeamFromListOfTeams(user.value.playerId, [props.fixture?.homeTeam, props.fixture?.awayTeam].filter(Boolean))
        : null);
const scheduleState = computed(() => fixtureScheduleState(props.fixture, myTeam.value?.id));
const proposedByMe = computed(() => scheduleState.value === 'minePending');
const proposedByOpponent = computed(() => scheduleState.value === 'theirsPending');
const scheduleActionLabel = computed(() => fixtureScheduleActionLabel(scheduleState.value));

// A ladder match's time buttons use its challenge page's words
// (utils/challengeSchedule.js): Suggest a time / Change time / Confirm time +
// Suggest another / Set a time / Reschedule. Other matches keep the generic one.
const ladderTimeActions = computed(() => {
  const f = props.fixture;
  if (!f?.ladderChallengeId) return null;
  const clubAdmin = isAuthenticated.value && f.clubId != null && isAdminOfClub(f.clubId);
  const s = ladderFixtureSchedule(f, ladderFixtureViewer(f, myTeam.value?.id ?? null, clubAdmin));
  if (!s) return null;
  return s.actions.filter(a => a.id === 'confirmTime' || a.id === 'schedule');
});
// Confirm time opens the dialog on the proposal (its title then reads
// "Confirm time"); every other button tells the dialog which one it was.
const timeActionRoute = (a) => ({...route, query: {...route.query, schedulingFixtureId: props.fixture.id,
  ...(a.id === 'schedule' && a.mode ? {scheduleMode: a.mode} : {})}});

// Never offer (re)scheduling once a result exists — a played match can't be scheduled, and
// the backend rejects it. Use the robust result check (isPlayed), not the API's `score`
// string, which is null on exactly the corrupt fixtures we're guarding against.
const canSchedule = computed(() => canEdit.value && !isPlayed.value);

// Meet fixtures follow their meet date — no schedule-editing affordance anywhere
// (the API rejects such reschedules too; change the date on the meet instead).
const isMeetLinked = computed(() => props.fixture?.meetOccurrenceId != null);

const hasTbcTeams = computed(() => {
  return !props.fixture?.homeTeam || !props.fixture?.awayTeam;
});

const hasBothTeams = computed(() => {
  return props.fixture?.homeTeamId && props.fixture?.awayTeamId;
});

// presentation only: winner reads ink, loser reads subtle (mockup .score .n.win/.n.loss)
const resultNumbers = computed(() => {
  const h = Number(props.fixture?.homeTeamResult);
  const a = Number(props.fixture?.awayTeamResult);
  if (Number.isFinite(h) && Number.isFinite(a)) return {home: h, away: a};
  return null;
});

const homeResultClass = computed(() => {
  if (!resultNumbers.value || resultNumbers.value.home === resultNumbers.value.away) return 'n-win';
  return resultNumbers.value.home > resultNumbers.value.away ? 'n-win' : 'n-loss';
});

const awayResultClass = computed(() => {
  if (!resultNumbers.value || resultNumbers.value.home === resultNumbers.value.away) return 'n-win';
  return resultNumbers.value.away > resultNumbers.value.home ? 'n-win' : 'n-loss';
});

onMounted(() => {
  addListener('fixtureUpdated', () => {
    editing.value = false;
  });
});

onBeforeUnmount(() => {
  removeLocalListenersWithEvent('fixtureUpdated');
});
</script>

<template>
  <div class="fixture-card"
       :class="[
         bordered ? 'fixture-card--bordered' : '',
         navigable ? 'fixture-cell-navigable' : '',
         hasTbcTeams ? 'opacity-70' : ''
       ]"
       @click="navigable && navigateToFixture()">

    <!-- ======== META (top): competition + status chips ======== -->
    <div v-if="!simple" class="fixture-meta d-flex align-center flex-wrap ga-1"
         :class="size === 'large' ? 'pa-3 pa-lg-4' : 'pa-3'">
      <v-chip variant="flat" class="tl-chip tl-chip--neutral" v-if="fixture.leagueName && fixture.leagueCycleDivisionName"
              :to="{name : 'LeagueDetail', params : {leagueId : fixture.leagueId, cycleId : fixture.leagueCycleId }}"
              size="small" @click.stop>
        <v-icon size="13" class="mr-1">mdi-table</v-icon>
        {{ fixture.leagueName + " · " + fixture.leagueCycleDivisionName }}
      </v-chip>
      <v-chip variant="flat" :to="{name : 'LadderDetail', params : {ladderId : fixture.ladderId}}"
              class="tl-chip tl-chip--neutral"
              v-if="fixture.ladderName && fixture.ladderId" size="small" @click.stop>
        <v-icon size="13" class="mr-1">mdi-ladder</v-icon>
        {{ fixture.ladderName }}
      </v-chip>
      <v-chip variant="flat" class="tl-chip tl-chip--neutral" :to="{name : 'TournamentDetail', params : {tournamentId : fixture.tournamentId}}"
              v-if="fixture.tournamentName" size="small" @click.stop>
        <v-icon size="13" class="mr-1">mdi-trophy</v-icon>
        {{ fixture.tournamentName }} · {{ fixture.tournamentStageName }}
      </v-chip>
      <!-- meet matches link to their event — never left looking like a friendly -->
      <v-chip variant="flat" class="tl-chip tl-chip--neutral"
              :to="{name: 'MeetOccurrenceDetail', params: {meetId: fixture.meetId, occurrenceId: fixture.meetOccurrenceId}}"
              v-if="fixture.meetName && fixture.meetOccurrenceId" size="small" @click.stop>
        <v-icon size="13" class="mr-1" color="success">mdi-handshake</v-icon>
        {{ fixture.meetName }}
      </v-chip>
      <!-- americano matches link back to their league's cycle -->
      <v-chip variant="flat" class="tl-chip tl-chip--neutral"
              :to="{name: 'AmericanoDetail', params: {americanoId: fixture.americanoId, cycleId: fixture.americanoCycleId}}"
              v-if="fixture.americanoName && fixture.americanoCycleId" size="small" @click.stop>
        <v-icon size="13" class="mr-1" color="#7C3AED">mdi-account-switch</v-icon>
        {{ fixture.americanoName }}<template v-if="fixture.americanoRound"> - Round {{ fixture.americanoRound }}</template>
      </v-chip>

      <v-spacer></v-spacer>

      <!-- status: played date / awaiting approval / scheduled / proposed -->
      <v-chip v-if="isPlayed && isFixtureFullyApproved(fixture)" variant="flat" size="small" class="tl-chip tl-chip--ok">
        <v-icon size="13" class="mr-1">mdi-check-circle</v-icon>
        {{ playedDateLabel }}
      </v-chip>
      <v-chip v-else-if="isPlayed && ((isFixtureAdmin) || fixtureNeedsApprovalFromPlayerId(fixture, user.playerId))"
              variant="flat" size="small" class="tl-chip tl-chip--brand"
              :to="{...route, query:{...route.query, editingFixtureId : fixture.id}}" @click.stop>
        <v-icon size="13" class="mr-1">mdi-alert-circle</v-icon>
        Awaiting your approval
      </v-chip>
      <v-chip v-else-if="isPlayed" variant="flat" size="small" class="tl-chip tl-chip--warn">
        <v-icon size="13" class="mr-1">mdi-clock-outline</v-icon>
        Awaiting opponent
      </v-chip>
      <!-- Schedule chips are list-context only: the detail page (size=large)
           shows date, status and playing area in its "When & where" section. -->
      <v-chip v-else-if="hasConfirmedSchedule && size !== 'large'" variant="flat" size="small" class="tl-chip tl-chip--ok">
        <v-icon size="13" class="mr-1">mdi-calendar-check</v-icon>
        {{ shortDate(scheduleDateToShow) }}<template v-if="fixture.playingAreaName"> · {{ fixture.playingAreaName }}</template>
      </v-chip>
      <v-chip v-else-if="hasProposedSchedule && size !== 'large'" variant="flat" size="small"
              :class="proposedByOpponent ? 'tl-chip tl-chip--brand' : 'tl-chip tl-chip--warn'">
        <v-icon size="13" class="mr-1">mdi-calendar-clock</v-icon>
        {{ proposedByOpponent ? 'They proposed:' : proposedByMe ? 'You proposed:' : 'Proposed:' }}
        {{ shortDate(scheduleDateToShow) }}<template v-if="fixture.playingAreaName"> · {{ fixture.playingAreaName }}</template>
      </v-chip>
    </div>

    <div :class="'text-center d-flex align-center ' + (size === 'large' ? 'px-4 pb-5 pa-lg-5 pt-lg-2' : 'px-4 pb-5 pt-2')">
      <div class="flex-fill v-col-6 pa-0 fixture-team-col">
        <div v-if="isPlayed" :class="['score-n d-flex align-center justify-center', homeResultClass, size === 'large' ? 'score-n--large' : '']">
          <v-badge :model-value="homeLadderPosition != null" color="transparent" offset-x="-15">
            <template  #badge>
              <fixture-ladder-position-change :ladder-position-change="homeLadderPosition"
                                              v-if="homeLadderPosition"/>
            </template>
            {{ fixture.homeTeamResult }}
          </v-badge>
        </div>
        <div v-else class="text-medium-emphasis d-flex align-center justify-center">
          <v-icon :size="size === 'large' ? 32 : 20">mdi-calendar-clock</v-icon>
        </div>

        <div class="d-flex justify-center pt-4 fixture-team-name">
          <team-card v-if="fixture.homeTeam" :size="size" :responsive="size === 'large'" :team="fixture.homeTeam" :max-lines="2"/>
          <div v-else class="text-medium-emphasis" :class="size === 'large' ? 'text-h6' : 'text-body-2'">
            TBC
          </div>
        </div>
      </div>
      <div v-if="isPlayed" class="score-sep">–</div>
      <div class="flex-fill v-col-6 pa-0 fixture-team-col">
        <div v-if="isPlayed" :class="['score-n d-flex align-center justify-center', awayResultClass, size === 'large' ? 'score-n--large' : '']">
          <v-badge :model-value="awayLadderPosition != null" color="transparent" offset-x="-15">
            <template #badge>
              <fixture-ladder-position-change  :ladder-position-change="awayLadderPosition"
                                              v-if="awayLadderPosition"/>
            </template>
            {{ fixture.awayTeamResult }}
          </v-badge>
        </div>
        <div v-else class="text-medium-emphasis d-flex align-center justify-center">
          <v-icon :size="size === 'large' ? 32 : 20">mdi-calendar-clock</v-icon>
        </div>

        <div class="d-flex justify-center pt-4 fixture-team-name">
          <team-card v-if="fixture.awayTeam" :size="size" :responsive="size === 'large'" :team="fixture.awayTeam" :max-lines="2"/>
          <div v-else class="text-medium-emphasis" :class="size === 'large' ? 'text-h6' : 'text-body-2'">
            TBC
          </div>
        </div>
      </div>
    </div>
    <!-- under the result: a page puts what it wants here (the match page: the external service marks) -->
    <slot name="after-result"/>
    <div :class="'hairline-top games d-flex justify-center flex-wrap ' + (size === 'large' ? 'pa-3 pa-lg-4' : 'pa-3')"
         v-if="fixture.games && fixture.games.length > 0 && breakdown">
      <span v-for="game in fixture.games"
            class="g"
            :class="{'g--w': game.homeTeamScore > game.awayTeamScore}">
        {{ gameScoreText(game.homeTeamScore, game.awayTeamScore) }}
      </span>
    </div>
    <!-- ======== FOOT: breakdown toggle + subtle actions ======== -->
    <div v-if="!simple && (showEdit || (fixture.games && fixture.games.length > 0))"
         class="fixture-foot hairline-top d-flex align-center flex-wrap ga-2"
         :class="size === 'large' ? 'pa-3 pa-lg-4' : 'pa-3'">

      <!-- breakdown toggle (left, subtle) -->
      <v-btn v-if="fixture.games && fixture.games.length > 0" @click.stop="breakdown = !breakdown"
             variant="text" size="small" class="text-none" color="charcoal">
        <v-icon size="16" start>mdi-format-list-bulleted</v-icon>
        {{ breakdown ? 'Hide' : 'Show' }} breakdown
      </v-btn>

      <!-- schedule / reschedule (left, ghost) — never offered on meet fixtures,
           whose date is the meet occurrence's date -->
      <template v-if="showEdit && !isPlayed && !isMeetLinked && ladderTimeActions">
        <v-btn v-for="a in ladderTimeActions" :key="a.id + a.label" :disabled="!canSchedule" variant="text" size="small" class="text-none"
               color="charcoal" :to="timeActionRoute(a)" @click.stop>
          <v-icon size="16" start>{{ a.id === 'confirmTime' ? 'mdi-calendar-check' : 'mdi-calendar-clock' }}</v-icon>
          {{ a.label }}
        </v-btn>
      </template>
      <v-btn v-else-if="showEdit && !isPlayed && !isMeetLinked" :disabled="!canSchedule" variant="text" size="small" class="text-none"
             color="charcoal" :to="{...route, query:{...route.query, schedulingFixtureId : fixture.id}}" @click.stop>
        <v-icon size="16" start>mdi-calendar-clock</v-icon>
        {{ scheduleActionLabel }}
      </v-btn>
      <!-- Generated matches (tournament draws AND americano rotations) only
           lose their time; league and ladder matches are DELETED (a ladder's
           challenge too), so each action is named for what it does (audit A4). -->
      <v-btn v-if="showEdit && hasSchedule && !isPlayed && !isMeetLinked && (canEdit || isFixtureAdmin) && isGeneratedMatch(fixture)"
             variant="text" size="small" class="text-none" color="charcoal"
             :to="{...route, query:{...route.query, clearingScheduleFixtureId : fixture.id}}" @click.stop>
        <v-icon size="16" start>mdi-calendar-remove</v-icon>
        Clear time
      </v-btn>
      <!-- An arranged ladder match: club admins and both sides' players (owner,
           3 Nov 2026). It cancels the challenge too; for the challenged side
           that counts as declining, and the confirm says the penalty. -->
      <v-btn v-else-if="showEdit && hasSchedule && !isPlayed && !isMeetLinked && canDeleteUnplayedMatch(fixture, {isAdmin: isFixtureAdmin, canEdit, isPlayer: !!myTeam})"
             variant="text" size="small" class="text-none" color="error"
             :to="{...route, query:{...route.query, cancelingScheduleFixtureId : fixture.id}}" @click.stop>
        <v-icon size="16" start>mdi-delete</v-icon>
        Delete match
      </v-btn>
      <!-- A meet match: the club admin removes it whatever its state, result and
           all (owner, 11 Oct 2026); members clear the result instead. -->
      <v-btn v-if="showEdit && isMeetLinked && canDeleteMeetMatch(fixture, {isAdmin: isFixtureAdmin})"
             variant="text" size="small" class="text-none" color="error"
             :to="{...route, query:{...route.query, cancelingScheduleFixtureId : fixture.id}}" @click.stop>
        <v-icon size="16" start>mdi-delete</v-icon>
        Delete match
      </v-btn>

      <v-spacer></v-spacer>

      <span v-if="showEdit && noEditReason" class="text-caption text-medium-emphasis mr-1">{{ noEditReason }}</span>

      <!-- clear result: admin-only, generated fixtures only (tournament draws and
           americano whist rotations — the fixture is kept and only the score is
           removed; other types are deleted). -->
      <v-btn v-if="isFixtureAdmin && isPlayed && (fixture.tournamentStageId || fixture.americanoCycleDivisionId || isMeetLinked)"
             variant="text" size="small" class="text-none" color="error"
             :to="{...route, query: {...route.query, clearingResultFixtureId : fixture.id}}" @click.stop>
        Clear result
      </v-btn>
      <!-- delete: only fixtures that aren't part of a generated structure -->
      <!-- generated match-ups (tournament, americano, meet) are cleared, never deleted -->
      <v-btn v-if="showEdit && isPlayed && !fixture.tournamentStageId && !fixture.americanoCycleDivisionId && !isMeetLinked" :disabled="!canEdit"
             variant="text" size="small" class="text-none" color="error"
             :to="{...route, query: {...route.query, deletingFixtureId : fixture.id}}" @click.stop>
        Delete
      </v-btn>

      <!-- live scoring: unplayed live-capable fixtures (icon-only on small cells) -->
      <v-btn v-if="showEdit && hasBothTeams && !isPlayed && fixture.supportsLiveScoring"
             :disabled="!canEdit"
             variant="outlined" size="small" class="text-none"
             :to="`/fixtures/${fixture.id}/live`" @click.stop>
        <v-icon size="16" :start="size === 'large'">mdi-scoreboard-outline</v-icon>
        <template v-if="size === 'large'">Live score</template>
        <v-tooltip v-if="size !== 'large'" activator="parent" location="top">Live score</v-tooltip>
      </v-btn>

      <!-- primary: record / edit result (right, filled) -->
      <v-btn v-if="showEdit && hasBothTeams" :disabled="!canEdit"
             variant="flat" size="small" color="primary" class="text-none"
             :to="{...route, query:{...route.query, editingFixtureId : fixture.id}}" @click.stop>
        <v-icon size="16" start>{{ fixtureResultActionIcon(resultAction) }}</v-icon>
        {{ fixtureResultActionLabel(resultAction) }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fixture-card {
  background: rgb(var(--v-theme-surface));
  border-radius: 14px;
  overflow: hidden;
}

/* opt-in hairline border for standalone placements */
.fixture-card--bordered {
  border: 1px solid rgb(var(--v-theme-borderLight));
}

/* keep the two halves shrinkable so long team names ellipsis instead of
   forcing the cell wider / colliding with the centre separator */
.fixture-team-col {
  min-width: 0;
}

.fixture-team-name {
  min-width: 0;
}

/* truncate long (doubles) team names to a tidy 2-line ellipsis: every link in
   the flex chain has to allow shrinking below its content for the clamp to bite */
.fixture-team-name > * {
  min-width: 0;
  max-width: 100%;
}

.fixture-team-name :deep(.v-badge),
.fixture-team-name :deep(.v-badge__wrapper),
.fixture-team-name :deep(.team-card-name--small),
.fixture-team-name :deep(.team-card-name--medium),
.fixture-team-name :deep(.team-card-name--large),
.fixture-team-name :deep(.team-card-name--responsive) {
  min-width: 0;
  max-width: 100%;
}

.fixture-team-name :deep(.line-clamp-2) {
  overflow-wrap: anywhere;
}

.hairline-top {
  border-top: solid 1px rgb(var(--v-theme-borderLight));
}

.fixture-actions {
  border-bottom: solid 1px rgb(var(--v-theme-borderLight));
  row-gap: 4px;
}

.fixture-cell-navigable {
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.fixture-cell-navigable:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* big result numbers — winner ink, loser subtle */
.score-n {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1.2;
}

.score-n--large {
  font-size: 30px;
}

.score-n.n-loss {
  color: rgb(var(--v-theme-textSubtle));
}

.score-sep {
  font-size: 18px;
  color: rgb(var(--v-theme-textSubtle));
  font-weight: 400;
  padding: 0 4px;
  align-self: flex-start;
  line-height: 1.6;
}

/* game breakdown pills */
.games {
  gap: 6px;
}

.games .g {
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
  background: rgb(var(--v-theme-light));
  border-radius: 7px;
  padding: 4px 8px;

  &.g--w {
    color: rgb(var(--v-theme-success));
    background: rgb(var(--v-theme-successTint));
  }
}

/* status / context / action chips — tint vocabulary */
.tl-chip {
  font-size: 11px;
  font-weight: 600;
  background: rgb(var(--v-theme-light)) !important;
  color: rgb(var(--v-theme-charcoal)) !important;
  border: 1px solid rgb(var(--v-theme-borderLight));

  &.tl-chip--ok {
    background: rgb(var(--v-theme-successTint)) !important;
    color: rgb(var(--v-theme-success)) !important;
    border-color: transparent;
  }

  &.tl-chip--warn {
    background: rgb(var(--v-theme-warningTint)) !important;
    color: rgb(var(--v-theme-warning)) !important;
    border-color: transparent;
  }

  &.tl-chip--brand {
    background: rgb(var(--v-theme-brandTint)) !important;
    color: rgb(var(--v-theme-primary)) !important;
    border-color: transparent;
  }

  &.tl-chip--primary {
    background: rgb(var(--v-theme-primary)) !important;
    color: #fff !important;
    border-color: transparent;
  }

  &.tl-chip--neutral {
    /* base neutral style above */
  }
}
</style>
